// Página StockTransfer - Transferencias de stock entre depósitos
// Permite transferir stock de un depósito a otro

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAllWarehouses } from '../../services/warehouses.service'
import { getCollection } from '../../services/firestore.service'
import { getStock, transferStock } from '../../services/stock.service'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './StockTransfer.css'

/**
 * Página StockTransfer
 * Transferencias de stock entre depósitos
 */
function StockTransfer() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()

  const [warehouses, setWarehouses] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Formulario
  const [formData, setFormData] = useState({
    productId: '',
    fromWarehouseId: '',
    toWarehouseId: '',
    quantity: '',
    reason: ''
  })

  // Stock disponible
  const [availableStock, setAvailableStock] = useState(null)
  const [checkingStock, setCheckingStock] = useState(false)

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true)
      const [warehousesData, productsData] = await Promise.all([
        getAllWarehouses(),
        getCollection('products', [
          { field: 'active', operator: '==', value: true }
        ])
      ])
      setWarehouses(warehousesData)
      setProducts(productsData)
    } catch (err) {
      console.error('Error cargando datos:', err)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [])

  const checkAvailableStock = useCallback(async () => {
    try {
      setCheckingStock(true)
      const stock = await getStock(formData.productId, formData.fromWarehouseId)
      setAvailableStock(stock)
    } catch (err) {
      console.error('Error verificando stock:', err)
      setAvailableStock(null)
    } finally {
      setCheckingStock(false)
    }
  }, [formData.productId, formData.fromWarehouseId])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData])

  useEffect(() => {
    if (formData.productId && formData.fromWarehouseId) {
      checkAvailableStock()
    } else {
      setAvailableStock(null)
    }
  }, [formData.productId, formData.fromWarehouseId, checkAvailableStock])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validaciones
    if (!formData.productId) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar un producto'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!formData.fromWarehouseId) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar el depósito origen'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!formData.toWarehouseId) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar el depósito destino'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (formData.fromWarehouseId === formData.toWarehouseId) {
      setNotification({
        type: 'error',
        message: 'El depósito origen y destino no pueden ser el mismo'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const quantity = parseFloat(formData.quantity)
    if (isNaN(quantity) || quantity <= 0) {
      setNotification({
        type: 'error',
        message: 'La cantidad debe ser un número mayor a 0'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!availableStock || availableStock.quantity < quantity) {
      setNotification({
        type: 'error',
        message: `Stock insuficiente. Disponible: ${availableStock?.quantity || 0}`
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      await transferStock(
        formData.productId,
        formData.fromWarehouseId,
        formData.toWarehouseId,
        quantity,
        formData.reason || '',
        currentUser?.uid || null
      )

      setNotification({
        type: 'success',
        message: `Transferencia de ${quantity} unidades realizada correctamente`
      })

      // Limpiar formulario
      setFormData({
        productId: '',
        fromWarehouseId: '',
        toWarehouseId: '',
        quantity: '',
        reason: ''
      })
      setAvailableStock(null)

      // Redirigir a la página de stock
      setTimeout(() => {
        navigate('/admin/stock')
      }, 1500)
    } catch (error) {
      console.error('Error realizando transferencia:', error)
      setNotification({
        type: 'error',
        message: `Error al realizar transferencia: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (loading) {
    return (
      <div className="stock-transfer-page">
        <div className="loading-state">
          <p>Cargando datos...</p>
        </div>
      </div>
    )
  }

  const fromWarehouseName = warehouses.find(w => w.id === formData.fromWarehouseId)?.name || ''
  const toWarehouseName = warehouses.find(w => w.id === formData.toWarehouseId)?.name || ''

  return (
    <div className="stock-transfer-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="transfer-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/stock')}
          >
            ← Volver
          </button>
          <h1>Transferencia de Stock</h1>
          <p className="transfer-subtitle">
            Transfiere stock de un depósito a otro
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="transfer-form-section">
        <form onSubmit={handleSubmit} className="transfer-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="productId">
                Producto <span className="required">*</span>
              </label>
              <select
                id="productId"
                name="productId"
                value={formData.productId}
                onChange={handleChange}
                required
                disabled={saving}
                className="form-input"
              >
                <option value="">Seleccionar producto...</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fromWarehouseId">
                Depósito Origen <span className="required">*</span>
              </label>
              <select
                id="fromWarehouseId"
                name="fromWarehouseId"
                value={formData.fromWarehouseId}
                onChange={handleChange}
                required
                disabled={saving}
                className="form-input"
              >
                <option value="">Seleccionar depósito origen...</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="toWarehouseId">
                Depósito Destino <span className="required">*</span>
              </label>
              <select
                id="toWarehouseId"
                name="toWarehouseId"
                value={formData.toWarehouseId}
                onChange={handleChange}
                required
                disabled={saving || !formData.fromWarehouseId}
                className="form-input"
              >
                <option value="">Seleccionar depósito destino...</option>
                {warehouses
                  .filter(w => w.id !== formData.fromWarehouseId)
                  .map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="quantity">
                Cantidad a Transferir <span className="required">*</span>
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0"
                min="1"
                step="1"
                max={availableStock?.quantity || undefined}
                required
                disabled={saving || !availableStock}
                className="form-input"
              />
              {checkingStock && (
                <small className="form-hint">Verificando stock disponible...</small>
              )}
              {availableStock !== null && !checkingStock && (
                <small className="form-hint">
                  Stock disponible en {fromWarehouseName}: <strong>{availableStock.quantity}</strong>
                </small>
              )}
              {availableStock === null && formData.productId && formData.fromWarehouseId && !checkingStock && (
                <small className="form-hint error-hint">
                  No hay stock disponible en este depósito
                </small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="reason">Motivo / Observaciones</label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder="Ej: Transferencia a sucursal, Reorganización de inventario..."
              rows={3}
              disabled={saving}
              className="form-input"
            />
          </div>

          {/* Resumen de transferencia */}
          {formData.productId && formData.fromWarehouseId && formData.toWarehouseId && formData.quantity && (
            <div className="transfer-summary">
              <h3 className="summary-title">Resumen de Transferencia</h3>
              <div className="summary-content">
                <div className="summary-item">
                  <span className="summary-label">Producto:</span>
                  <span className="summary-value">
                    {products.find(p => p.id === formData.productId)?.name || 'N/A'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Desde:</span>
                  <span className="summary-value">{fromWarehouseName}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Hacia:</span>
                  <span className="summary-value">{toWarehouseName}</span>
                </div>
                <div className="summary-item highlight">
                  <span className="summary-label">Cantidad:</span>
                  <span className="summary-value">{formData.quantity} unidades</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/stock')}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !availableStock || (availableStock && parseFloat(formData.quantity) > availableStock.quantity)}
            >
              {saving ? 'Transfiriendo...' : 'Realizar Transferencia'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default StockTransfer

